<script setup lang="ts">
import {computed, inject} from 'vue'
import type {usePrincipalStore} from '@/stores/iam/principal-store'
import {useLocalization} from '@hantera/portal-app'
import SessionList from '../Shared/SessionList.vue'
import {Badge} from '@hantera/design-system'

const store = inject<ReturnType<typeof usePrincipalStore>>('principalStore')!
const t = useLocalization('iam.principals')

// Count only revokable sessions (not current, not already revoked)
const revokableSessions = computed(() => {
  return store.sessions.filter((s) => !s.isCurrent && !s.isRevoked)
})

async function handleRevokeSession(sessionId: string) {
  const confirmed = await store.dialogs.confirm(
    t('sessions.revokeSessionPrompt'),
    t('sessions.revokeSessionTitle')
  )

  if (!confirmed) return

  try {
    const principalId = store.principal?.id
    if (!principalId) return

    await store.iamService.revokePrincipalSession(principalId, sessionId)
    await store.refresh()
    store.processes.createProcess({
      description: t('sessions.sessionRevoked'),
    }).complete()
  } catch (error: any) {
    console.error('Failed to revoke session:', error)
    store.processes.error({
      message: error.response?.data?.message || t('sessions.failedToRevokeSession'),
    })
  }
}

const activeSessions = computed(() => {
  return store.sessions.filter(r => !r.isRevoked && new Date() < r.accessTokenExpiresAt)
})

const expiredSessions = computed(() => {
  return store.sessions.filter(r => r.isRevoked || r.accessTokenExpiresAt <= new Date())
})

</script>

<template>
  
  <div class="sessions-tab">
    <div class="sessions-header">
      <h3>{{ t('sessions.activeSessions') }}</h3>
      <div class="header-actions">
        <Badge>{{ activeSessions.length === 1 ? t('sessions.sessionCountSingular') : t('sessions.sessionCountPlural', { count: activeSessions.length }) }}</Badge>
      </div>
    </div>

    <div v-if="activeSessions.length === 0" class="empty-state">
      <p>{{ t('sessions.noActiveSessions') }}</p>
      <p class="hint">{{ t('sessions.userNotLoggedIn') }}</p>
    </div>

    <SessionList
      v-else
      :sessions="activeSessions"
      :can-revoke="store.canWrite"
      @revoke="handleRevokeSession"
    />
  </div>

  <div class="sessions-tab" v-if="expiredSessions.length > 0">
    <div class="sessions-header">
      <h3>{{ t('sessions.expiredSessions') }}</h3>
      <div class="header-actions">
        <Badge>{{ expiredSessions.length === 1 ? t('sessions.sessionCountSingular') : t('sessions.sessionCountPlural', { count: expiredSessions.length }) }}</Badge>
      </div>
    </div>

    <SessionList
      :sessions="expiredSessions"
      :can-revoke="false"
      @revoke="handleRevokeSession"
    />
  </div>
</template>

<style scoped>
.sessions-tab {
  padding: 1.5rem;
}

.sessions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 1rem);
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xl, 2rem) var(--spacing-lg, 1.5rem);
  color: var(--color-text-secondary, #666);
}

.empty-state p {
  margin: var(--spacing-sm, 0.5rem) 0;
}

.empty-state .hint {
  font-size: var(--font-size-sm, 0.875rem);
  font-style: italic;
}
</style>
